import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { ArrowLeft, X, AlertCircle, Bookmark, BookmarkCheck, Eye, EyeOff, WifiOff, ChevronLeft, ChevronRight } from 'lucide-react';
import { fetchVerses, fetchChapters } from '../../services/quranAPI';
import SEO from '../common/SEO';
import { CrescentStarIcon, IslamicDivider } from '../common/IslamicIcons';
import { useQuranProgress, useScrollTracker } from '../../hooks/useQuranProgress';
import { useQuranPlayer } from '../../contexts/QuranPlayerContext';
import { usePrayer } from '../../contexts/PrayerContext';
import { getNextPrayer } from '../../utils/nextPrayer';
import QuranControlPanel from '../quran/QuranControlPanel';

const NASKH = "'Amiri', 'Noto Naskh Arabic', serif";
const toArabicNum = (n) => String(n).replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[d]);

const loadAllLastRead = () => {
  try { return JSON.parse(localStorage.getItem('quran_last_read') || '{}'); }
  catch { return {}; }
};

const SurahReaderPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isDarkMode, isRTL, language, formatTime } = useTheme();
  const { prayerTimes } = usePrayer();

  const chapterId = parseInt(id, 10);
  const autoplay = searchParams.get('autoplay') === '1';
  const untilPrayer = searchParams.get('until') === 'prayer';

  const {
    getLastRead, saveLastRead, clearLastRead,
    arabicOnly, toggleArabicOnly,
    viewMode, toggleViewMode,
    toggleBookmark, isBookmarked,
  } = useQuranProgress();

  const [chapter, setChapter] = useState(null);
  const [verses, setVerses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verseError, setVerseError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [resumeVerseKey, setResumeVerseKey] = useState(null);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [revealedVerses, setRevealedVerses] = useState(new Set());
  const [verseEndedTick, setVerseEndedTick] = useState(0);

  // Global (app-wide) audio player — keeps playing across navigation
  const {
    player,
    setVerses: setPlayerVerses,
    setChapter: setPlayerChapter,
    reciterId, setReciterId,
    readMode, setReadMode,
    setCallbacks,
  } = useQuranPlayer();

  // Feed this surah's data into the shared player
  useEffect(() => { setPlayerChapter(chapter); }, [chapter, setPlayerChapter]);
  useEffect(() => { setPlayerVerses(verses); }, [verses, setPlayerVerses]);

  // Register per-page audio callbacks (cleared on unmount so background audio
  // doesn't auto-advance surahs once the user has left the reader)
  useEffect(() => {
    setCallbacks(
      () => setVerseEndedTick(t => t + 1),
      () => {
        if (untilPrayer) {
          const next = chapterId >= 114 ? 1 : chapterId + 1;
          navigate(`/quran/surah/${next}?autoplay=1&until=prayer`);
        }
      },
    );
    return () => setCallbacks(null, null);
  }, [untilPrayer, chapterId, navigate, setCallbacks]);

  const resumeFromUrl = searchParams.get('resume');
  const verseRefs = useRef({});
  const resumeCheckedRef = useRef(false);

  useEffect(() => {
    fetchChapters().then(chs => setChapter(chs.find(c => c.id === chapterId))).catch(() => {});
  }, [chapterId]);

  // Load ALL verses at once (no pagination)
  useEffect(() => {
    const loadAllVerses = async () => {
      setLoading(true);
      setVerseError(false);
      setVerses([]);
      try {
        // First fetch to get total pages
        const firstPage = await fetchVerses(chapterId, 1, language);
        let allVerses = [...firstPage.verses];
        const totalPages = firstPage.pagination?.total_pages || 1;
        
        // Fetch remaining pages in parallel for speed
        if (totalPages > 1) {
          const pagePromises = [];
          for (let p = 2; p <= totalPages; p++) {
            pagePromises.push(fetchVerses(chapterId, p, language));
          }
          const results = await Promise.all(pagePromises);
          results.forEach(data => {
            allVerses = [...allVerses, ...data.verses];
          });
        }
        setVerses(allVerses);
      } catch (err) {
        setVerseError(true);
      } finally {
        setLoading(false);
      }
    };
    loadAllVerses();
  }, [chapterId, language, reloadKey]);

  useEffect(() => {
    if (resumeCheckedRef.current) return;
    resumeCheckedRef.current = true;
    if (autoplay) return; // don't interrupt autoplay with a resume prompt
    const stored = loadAllLastRead()[chapterId];
    if (resumeFromUrl) { setResumeVerseKey(resumeFromUrl); } // explicit resume → auto-continue (no prompt)
    else if (stored && stored.verse_number > 1) { setResumeVerseKey(stored.verse_key); setShowResumePrompt(true); }
  }, []);

  const handleVerseVisible = useCallback((verseKey, verseNumber) => {
    if (chapter) saveLastRead(chapterId, chapter.name_simple, verseKey, verseNumber);
  }, [chapterId, chapter, saveLastRead]);

  const { setRef } = useScrollTracker(verses, handleVerseVisible);

  const scrollToVerse = useCallback((verseKey, opts = {}) => {
    const { smooth = true, retry = false } = opts;
    let attempts = 0;
    const doScroll = () => {
      const el = verseRefs.current[verseKey];
      if (el) el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' });
      attempts += 1;
      // Retry a few times to survive the large-surah reflow on cold load
      if (retry && attempts < 6) setTimeout(doScroll, 300);
    };
    const start = () => setTimeout(doScroll, retry ? 120 : 300);
    if (retry && document.fonts?.ready) document.fonts.ready.then(start).catch(start);
    else start();
  }, []);

  useEffect(() => {
    if (player.currentVerse) scrollToVerse(player.currentVerse.verse_key);
  }, [player.currentVerse?.verse_key, scrollToVerse]);

  const resetPractice = () => setRevealedVerses(new Set());

  const toggleRevealVerse = (verseKey) => {
    setRevealedVerses(prev => { const next = new Set(prev); next.has(verseKey) ? next.delete(verseKey) : next.add(verseKey); return next; });
  };

  // Recitation mode: pause after verse ends, wait 4s then auto-advance
  // The verseEndedTick state increments when a verse finishes, triggering this effect
  useEffect(() => {
    if (readMode !== 'recitation' || !player.currentVerse || verseEndedTick === 0) return;
    // Only schedule advance if audio is not playing (verse just ended)
    if (player.playing) return;
    // Wait 4 seconds for user to recite, then advance
    const timer = setTimeout(() => player.playNext(), 4000);
    return () => clearTimeout(timer);
  }, [readMode, verseEndedTick, player.currentVerse?.verse_key, player.playing, player.playNext]);

  const isMushaf = viewMode === 'mushaf';
  const showTranslation = !arabicOnly && !isMushaf && readMode === 'reading';

  // ── Navigate between surahs (multiple entry points: header + footer) ──
  const goToSurah = useCallback((delta) => {
    const target = chapterId + delta;
    if (target < 1 || target > 114) return;
    player.stop();
    window.scrollTo({ top: 0 });
    navigate(`/quran/surah/${target}`);
  }, [chapterId, navigate, player]);

  // Swipe left/right to move between surahs
  const touchRef = useRef(null);
  const onTouchStart = (e) => { const tt = e.changedTouches[0]; touchRef.current = { x: tt.clientX, y: tt.clientY, t: Date.now() }; };
  const onTouchEnd = (e) => {
    const s = touchRef.current; if (!s) return; touchRef.current = null;
    const tt = e.changedTouches[0];
    const dx = tt.clientX - s.x, dy = tt.clientY - s.y;
    if (Date.now() - s.t < 700 && Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 1.8) {
      if (dx < 0) goToSurah(isRTL ? -1 : 1);
      else goToSurah(isRTL ? 1 : -1);
    }
  };

  // ── Autoplay (from "Read me a Surah" / "Play until prayer") ──
  const autoStartedRef = useRef(false);
  const resumeScrolledRef = useRef(false);
  const pendingResumePlayRef = useRef(false);
  useEffect(() => { autoStartedRef.current = false; resumeScrolledRef.current = false; }, [chapterId]);
  useEffect(() => {
    if (autoplay && !autoStartedRef.current && verses.length > 0 && !loading) {
      autoStartedRef.current = true;
      player.playSurah(0);
    }
  }, [autoplay, verses.length, loading, chapterId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Resume: jump to where the user stopped and continue playing ──
  useEffect(() => {
    if (resumeScrolledRef.current) return;
    if (!loading && verses.length > 0 && resumeVerseKey) {
      resumeScrolledRef.current = true;
      scrollToVerse(resumeVerseKey, { smooth: false, retry: true });
      // Explicit "Resume" (via ?resume=) starts recitation from that verse once
      // the shared player has actually received this surah's verses.
      if (resumeFromUrl) pendingResumePlayRef.current = true;
    }
  }, [loading, verses.length, resumeVerseKey, resumeFromUrl, scrollToVerse]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fire resume playback only after the shared player is synced to this surah
  useEffect(() => {
    if (!pendingResumePlayRef.current) return;
    if (resumeVerseKey && verses.length > 0 && player.totalVerses === verses.length) {
      pendingResumePlayRef.current = false;
      player.playVerse(resumeVerseKey);
    }
  }, [player.totalVerses, verses.length, resumeVerseKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── "Play until next prayer" — auto-stop when the next adhan arrives ──
  const [untilBanner, setUntilBanner] = useState(null);
  useEffect(() => {
    if (!untilPrayer || !prayerTimes) { setUntilBanner(null); return; }
    const next = getNextPrayer(prayerTimes);
    if (!next) return;
    setUntilBanner({ name: next.name, time: formatTime(next.raw) });
    const ms = next.date - new Date();
    if (ms <= 0) return;
    const timer = setTimeout(() => { player.stop(); }, ms);
    return () => clearTimeout(timer);
  }, [untilPrayer, prayerTimes]); // eslint-disable-line react-hooks/exhaustive-deps

  const bgClass = isDarkMode
    ? 'bg-gradient-to-b from-[var(--bg-dark-via)] via-[var(--bg-dark-from)] to-[var(--bg-dark-anchor)]'
    : 'bg-gradient-to-b from-[var(--bg-light-via)] via-[var(--bg-light-from)] to-white';

  const mushafBg = isDarkMode ? 'bg-[#1a1208]' : 'bg-[#faf8f1]';

  return (
    <>
      <SEO title={chapter?.name_simple || 'Surah Reader'} path={`/quran/surah/${id}`} />
      <div className={`min-h-screen pb-36 ${isMushaf ? mushafBg : bgClass}`} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>

        {/* ── Combined sticky header: navigation + surah identity + play ── */}
        <div className="sticky top-14 z-30 backdrop-blur-md border-b"
          style={{
            borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
            background: isDarkMode
              ? 'linear-gradient(135deg, color-mix(in srgb, var(--bg-dark-from) 92%, transparent), color-mix(in srgb, var(--card-dark-from) 92%, transparent))'
              : 'linear-gradient(135deg, color-mix(in srgb, var(--bg-light-via) 94%, white), rgba(255,255,255,0.92))',
          }}>
          <div className={`px-3 py-2 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* left: back + prev */}
            <div className={`flex items-center gap-0.5 flex-shrink-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button onClick={() => navigate('/quran')} aria-label="Back"
                className={`p-2 rounded-xl ${isDarkMode ? 'text-gray-300 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-black/5'}`}>
                <ArrowLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
              </button>
              <button data-testid="header-prev-surah" onClick={() => goToSurah(-1)} disabled={chapterId <= 1} aria-label="Previous surah"
                className={`p-1.5 rounded-xl disabled:opacity-25 ${isDarkMode ? 'text-gray-300 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-black/5'}`}>
                <ChevronLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {/* center: surah identity */}
            {chapter && (
              <div className="flex-1 min-w-0 text-center px-1">
                <p className="font-bold truncate" style={{ color: isDarkMode ? '#fbbf24' : 'var(--p-hex)', fontFamily: NASKH, fontSize: '1.15rem', lineHeight: 1.2 }}>
                  {chapter.name_arabic}
                </p>
                <p className={`text-[10px] truncate flex items-center justify-center gap-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  {player.isOffline && <WifiOff className="w-2.5 h-2.5 text-amber-400" />}
                  {chapter.name_simple} · {chapter.verses_count} {t('quranReader.verses', 'verses')}
                </p>
              </div>
            )}

            {/* right: play/pause + next */}
            <div className={`flex items-center gap-1 flex-shrink-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button data-testid="play-surah-btn"
                onClick={() => (player.currentVerse ? player.togglePlay() : player.playSurah(0))}
                aria-label="Play surah"
                className="w-9 h-9 rounded-full flex items-center justify-center bg-amber-500 hover:bg-amber-400 text-[#0f172a] shadow-md shadow-amber-500/25 active:scale-95 transition-colors">
                {player.playing
                  ? <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor"><rect x="3" y="2" width="3.5" height="12" rx="1"/><rect x="9.5" y="2" width="3.5" height="12" rx="1"/></svg>
                  : <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor"><path d="M3 2.5v11l11-5.5z"/></svg>}
              </button>
              <button data-testid="header-next-surah" onClick={() => goToSurah(1)} disabled={chapterId >= 114} aria-label="Next surah"
                className={`p-1.5 rounded-xl disabled:opacity-25 ${isDarkMode ? 'text-gray-300 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-black/5'}`}>
                <ChevronRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 py-4 space-y-3">
          {/* "Play until next prayer" banner */}
          {untilBanner && (
            <div data-testid="until-prayer-banner" className={`rounded-2xl px-4 py-3 flex items-center gap-3 animate-fade-up ${
              isDarkMode ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-200'
            } ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: isDarkMode ? 'rgba(16,185,129,0.18)' : 'rgba(5,150,105,0.12)' }}>
                <svg className="w-4 h-4" viewBox="0 0 16 16" fill={isDarkMode ? '#6ee7b7' : '#059669'}><path d="M3 2.5v11l11-5.5z"/></svg>
              </div>
              <p className={`flex-1 text-xs font-semibold ${isDarkMode ? 'text-emerald-300' : 'text-emerald-800'}`}>
                {t('quranReader.playingUntil', 'Playing until')} {untilBanner.name} · <span dir="ltr">{untilBanner.time}</span>
              </p>
              <button data-testid="until-prayer-stop" onClick={() => { player.stop(); setUntilBanner(null); }}
                className={`text-xs font-bold px-2.5 py-1 rounded-lg ${isDarkMode ? 'bg-white/10 text-emerald-300' : 'bg-white text-emerald-700 shadow-sm'}`}>
                {t('quranReader.stop', 'Stop')}
              </button>
            </div>
          )}

          {/* Resume prompt */}
          {showResumePrompt && resumeVerseKey && (
            <div data-testid="resume-prompt" className={`rounded-2xl px-4 py-3 flex items-center gap-3 animate-fade-up ${
              isDarkMode ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'
            } ${isRTL ? 'flex-row-reverse' : ''}`}>
              <p className={`flex-1 text-xs font-medium ${isDarkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                {t('quranReader.continueReading')} — {resumeVerseKey}
              </p>
              <button onClick={() => { setShowResumePrompt(false); scrollToVerse(resumeVerseKey, { smooth: false, retry: true }); }}
                className={`text-xs font-bold px-2.5 py-1 rounded-lg ${isDarkMode ? 'bg-amber-500 text-[#0f172a]' : 'bg-amber-500 text-white'}`}>
                {t('quranReader.resumeBtn')}
              </button>
              <button onClick={() => { clearLastRead(chapterId); setShowResumePrompt(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('quranReader.startOver')}</button>
              <button data-testid="close-resume-prompt" onClick={() => setShowResumePrompt(false)}>
                <X className={`w-3.5 h-3.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              </button>
            </div>
          )}

          {/* Bismillah — the combined sticky header now carries the surah identity */}
          {chapter && chapterId !== 9 && chapterId !== 1 && (
            <p data-testid="bismillah-line" className="text-center font-semibold py-1"
              style={{ fontFamily: NASKH, direction: 'rtl', fontSize: '1.35rem', lineHeight: 2, color: isDarkMode ? '#fcd34d' : 'var(--p-hex)' }}>
              بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
            </p>
          )}

          {/* Mushaf flow view */}
          {isMushaf && verses.length > 0 && (
            <div className={`rounded-3xl p-5 ${isDarkMode ? 'bg-[#1a1208] border border-amber-800/20' : 'bg-[#faf8f1] border border-amber-200 shadow-inner'}`}>
              <p className="text-right leading-[3.2]"
                style={{ fontFamily: NASKH, direction: 'rtl', fontSize: '1.4rem', color: isDarkMode ? '#fcd34d' : '#2d1b00', textAlign: 'justify', textAlignLast: 'right' }}>
                {verses.map(verse => {
                  const isCurrentlyPlaying = player.currentVerse?.verse_key === verse.verse_key && player.playing;
                  return (
                    <React.Fragment key={verse.id}>
                      <span data-verse-key={verse.verse_key} data-verse-number={String(verse.verse_number)} data-testid={`verse-${verse.verse_key}`}
                        ref={el => { verseRefs.current[verse.verse_key] = el; setRef(verse.verse_key, el); }}
                        className={isCurrentlyPlaying ? 'bg-amber-400/20 rounded px-0.5' : (verse.verse_key === resumeVerseKey ? 'bg-amber-300/25 rounded px-0.5 ring-1 ring-amber-400/60' : '')}>
                        {verse.text_uthmani}
                      </span>
                      <span className="inline-flex items-center justify-center mx-1.5 align-middle cursor-pointer select-none"
                        style={{ width:'1.6em',height:'1.6em',borderRadius:'50%',background:isDarkMode?'rgba(251,191,36,0.15)':'rgba(180,120,0,0.12)',color:isDarkMode?'#fbbf24':'#92400e',fontSize:'0.6em',fontFamily:NASKH }}
                        onClick={() => player.playVerse(verse.verse_key)}>
                        {toArabicNum(verse.verse_number)}
                      </span>{' '}
                    </React.Fragment>
                  );
                })}
              </p>
            </div>
          )}

          {/* Card view */}
          {!isMushaf && verses.map((verse, idx) => {
            const isPlaying = player.playing && player.currentVerse?.verse_key === verse.verse_key;
            const isAudioLoading = player.loading && player.currentVerse?.verse_key === verse.verse_key;
            const bookmarked = isBookmarked(verse.verse_key);
            const isResumeTarget = verse.verse_key === resumeVerseKey;
            return (
              <div key={verse.id}
                data-testid={`verse-${verse.verse_key}`}
                ref={el => { verseRefs.current[verse.verse_key] = el; setRef(verse.verse_key, el); }}
                data-verse-key={verse.verse_key} data-verse-number={String(verse.verse_number)}
                className={`rounded-2xl overflow-hidden transition-all animate-fade-up ${isResumeTarget ? 'ring-2 ring-amber-400' : ''} ${
                  isPlaying
                    ? isDarkMode ? 'bg-amber-500/15 border border-amber-500/25 shadow-lg' : 'bg-amber-50 border border-amber-200 shadow-md'
                    : isDarkMode ? 'glass-card' : 'bg-white shadow-sm border border-gray-100'
                }`}
                style={{ animationDelay: `${Math.min(idx * 0.04, 0.6)}s` }}
              >
                <div className={`flex items-center justify-between px-4 py-2 border-b ${isDarkMode ? 'border-white/5' : 'border-gray-100'} ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <span className={`text-xs font-bold tabular-nums px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-white/10 text-gray-400' : 'bg-gray-100 text-gray-500'}`} dir="ltr">{verse.verse_key}</span>
                  <div className={`flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <button data-testid={`bookmark-${verse.verse_key}`}
                      onClick={() => toggleBookmark(chapterId, chapter?.name_simple, verse.verse_key, verse.verse_number)}
                      className={`p-1.5 rounded-full ${bookmarked ? isDarkMode ? 'text-amber-400 bg-amber-500/15' : 'text-amber-600 bg-amber-50' : isDarkMode ? 'text-gray-500 hover:text-amber-400' : 'text-gray-400 hover:text-amber-500'}`}>
                      {bookmarked ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
                    </button>
                    <button data-testid={`play-${verse.verse_key}`} onClick={() => player.playVerse(verse.verse_key)}
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${isPlaying ? 'bg-amber-500 text-[#0f172a] scale-110' : isDarkMode ? 'bg-white/10 text-gray-400 hover:bg-amber-500/20 hover:text-amber-400' : 'bg-gray-100 text-gray-500 hover:bg-amber-100 hover:text-amber-600'}`}>
                      {isAudioLoading ? <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        : isPlaying ? <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor"><rect x="3" y="2" width="3.5" height="12" rx="1"/><rect x="9.5" y="2" width="3.5" height="12" rx="1"/></svg>
                        : <svg className="w-3.5 h-3.5 ml-0.5" viewBox="0 0 16 16" fill="currentColor"><path d="M3 2.5v11l11-5.5z"/></svg>}
                    </button>
                  </div>
                </div>
                <div className="px-4 pt-4 pb-3">
                  {readMode === 'practice' && !revealedVerses.has(verse.verse_key) ? (
                    <button data-testid={`reveal-verse-${verse.verse_key}`} onClick={() => toggleRevealVerse(verse.verse_key)}
                      className={`w-full py-5 rounded-xl flex items-center justify-center gap-2 text-sm font-medium ${isDarkMode ? 'bg-violet-500/10 border border-violet-500/20 text-violet-400' : 'bg-violet-50 border border-violet-200 text-violet-700'}`}>
                      <Eye className="w-4 h-4" />{t('quranMode.revealVerse')}
                    </button>
                  ) : (
                    <p className="text-right leading-loose"
                      onClick={() => readMode === 'practice' ? toggleRevealVerse(verse.verse_key) : undefined}
                      style={{ fontFamily: NASKH, direction: 'rtl', fontSize: arabicOnly ? '1.35rem' : '1.2rem', lineHeight: '2.3', color: isDarkMode ? '#fcd34d' : 'var(--p-hex)', cursor: readMode === 'practice' ? 'pointer' : 'default' }}>
                      {verse.text_uthmani}
                    </p>
                  )}
                </div>
                {showTranslation && verse.translations?.[0]?.text && (
                  <div className="px-4 pb-4">
                    <IslamicDivider isDarkMode={isDarkMode} className="mb-2 opacity-25" />
                    <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {verse.translations[0].text.replace(/<sup[^>]*>[\s\S]*?<\/sup>/gi,'').replace(/<[^>]+>/g,'').trim()}
                    </p>
                  </div>
                )}
              </div>
            );
          })}

          {verseError && (
            <div data-testid="verse-error-box" className={`rounded-2xl p-6 text-center ${isDarkMode ? 'glass-card' : 'bg-white shadow-sm border border-gray-100'}`}>
              <AlertCircle className={`w-8 h-8 mx-auto mb-2 ${player.isOffline ? 'text-amber-400' : 'text-red-400'}`} />
              <p data-testid="verse-error-msg" className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {player.isOffline ? t('quranReader.offlineNotCached', 'This surah is not cached for offline use.') : t('quranReader.loadError', 'Failed to load verses.')}
              </p>
              {!player.isOffline && (
                <button onClick={() => { setVerseError(false); setReloadKey(k => k + 1); }} className="mt-3 px-4 py-2 rounded-xl bg-[var(--p-hex)] text-white text-sm">{t('quranReader.retry', 'Retry')}</button>
              )}
            </div>
          )}

          {loading && <div className="flex justify-center py-8"><div className="w-8 h-8 border-4 border-[var(--p-hex)] border-t-transparent rounded-full animate-spin" /></div>}

          {!loading && verses.length > 0 && (
            <div className="py-6 space-y-4">
              <div className="text-center">
                <CrescentStarIcon size={18} color={isDarkMode ? '#fbbf24' : 'var(--p-hex)'} className="mx-auto mb-2" />
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('quranReader.endOf', 'End of')} {chapter?.name_simple}</p>
              </div>
              <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <button data-testid="prev-surah-btn" onClick={() => goToSurah(-1)} disabled={chapterId <= 1}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-2xl font-semibold text-sm transition-colors disabled:opacity-30 ${
                    isDarkMode ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-white text-gray-700 hover:bg-gray-50 shadow-sm border border-gray-100'
                  }`}>
                  <ChevronLeft className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                  {t('quranReader.prevSurah', 'Previous')}
                </button>
                <button data-testid="next-surah-btn" onClick={() => goToSurah(1)} disabled={chapterId >= 114}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-2xl font-semibold text-sm transition-colors disabled:opacity-30 bg-[var(--p-hex)] text-white hover:bg-[var(--p-light-hex)] shadow-sm">
                  {t('quranReader.nextSurah', 'Next Surah')}
                  <ChevronRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Unified control panel (replaces QuranAudioBar + header buttons) ── */}
        <QuranControlPanel
          player={player}
          chapter={chapter}
          reciterId={reciterId}
          setReciterId={setReciterId}
          arabicOnly={arabicOnly}
          toggleArabicOnly={toggleArabicOnly}
          viewMode={viewMode}
          toggleViewMode={toggleViewMode}
          readMode={readMode}
          setReadMode={setReadMode}
          revealedVerses={revealedVerses}
          resetPractice={resetPractice}
          onVerseClick={scrollToVerse}
        />
      </div>
    </>
  );
};

export default SurahReaderPage;
